import { MediaCapture, MediaCaptureProps, MediaCaptureRef } from "@kalyzee/kast-web-sdk/web-components";

import React, { ForwardedRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { logger } from "../../helpers/logger";
import { getMediaConstraints, VideoQuality } from "../../helpers/videos";
import "./MediaSelector.css";
import { getMediaParam, MediaKeys, setMediaParam } from "../../helpers/storage";

export interface MediaSelectorProps {
  style?: React.CSSProperties;
  className?: string;
  display?: boolean;
  defaultInputAudioDeviceInfo?: MediaCaptureProps["defaultSelectAudioDevice"];
  defaultInputVideoDeviceInfo?: MediaCaptureProps["defaultSelectVideoDevice"];
  defaultOutputAudioDeviceInfo?: MediaCaptureProps["defaultSelectOutputAudioDevice"];
  displayVideo?: MediaCaptureProps["displayVideo"];
  displayAudioDeviceList?: MediaCaptureProps["displayAudioDeviceList"];
  displayOutputAudioDeviceList?: MediaCaptureProps["displayOutputAudioDeviceList"];
  audioStreamEnabled?: boolean;
  videoStreamEnabled?: boolean;
  quality?: VideoQuality;
  onMediaStream?: (media: MediaStream) => void;
  onInputAudioDeviceInfo?: (device?: MediaDeviceInfo) => void;
  onInputVideoDeviceInfo?: (device?: MediaDeviceInfo) => void;
  onOutputAudioDeviceInfo?: (device?: MediaDeviceInfo) => void;
  onAudioStreamEnabled?: (enabled: boolean) => void;
  onVideoStreamEnabled?: (enabled: boolean) => void;
  onMediaCaptureError?: (type: "audio" | "video", error: any) => void;
}

export const MediaSelector = React.memo(React.forwardRef(
  (
    {
      style,
      className,
      display = true,
      defaultInputAudioDeviceInfo,
      defaultInputVideoDeviceInfo,
      defaultOutputAudioDeviceInfo,
      displayVideo,
      displayAudioDeviceList,
      displayOutputAudioDeviceList,
      audioStreamEnabled,
      videoStreamEnabled,
      quality = "low",
      onMediaStream,
      onInputAudioDeviceInfo,
      onInputVideoDeviceInfo,
      onOutputAudioDeviceInfo,
      onAudioStreamEnabled,
      onVideoStreamEnabled,
      onMediaCaptureError,
    }: MediaSelectorProps,
    ref: ForwardedRef<MediaCaptureRef | undefined>
  ) => {
    const innerRef = useRef<MediaCaptureRef>();
    
    const internalDefaultInputVideoDeviceInfo = useMemo(() => {
      if (defaultInputVideoDeviceInfo) return defaultInputVideoDeviceInfo;
      const label = getMediaParam(MediaKeys.DEFAULT_INPUT_VIDEO_DEVICE);
      if (label) return { label };
      return undefined;
    }, [defaultInputVideoDeviceInfo]);

    const internalDefaultInputAudioDeviceInfo = useMemo(() => {
      if (defaultInputAudioDeviceInfo) return defaultInputAudioDeviceInfo;
      const label = getMediaParam(MediaKeys.DEFAULT_INPUT_AUDIO_DEVICE);
      if (label) return { label };
      return undefined;
    }, [defaultInputAudioDeviceInfo]);

    const internalDefaultOutputAudioDeviceInfo = useMemo(() => {
      if (defaultOutputAudioDeviceInfo) return defaultOutputAudioDeviceInfo;
      const label = getMediaParam(MediaKeys.DEFAULT_OUTPUT_AUDIO_DEVICE);
      if (label) return { label };
      return undefined;
    }, [defaultOutputAudioDeviceInfo]);

    const _style = style || {};
    if (!display && _style.display !== "none") {
      _style.display = "none";
    }

    useImperativeHandle(ref, () => innerRef.current);

    useEffect(() => {
      innerRef.current?.updateMediaConstraints(getMediaConstraints(quality));
    }, [quality]);


    return (
      <MediaCapture
        audioContext={getAudioContextForVolumeAnalyzer()}
        ref={innerRef}
        style={style}
        className={className}
        defaultSelectAudioDevice={internalDefaultInputAudioDeviceInfo}
        defaultSelectVideoDevice={internalDefaultInputVideoDeviceInfo}
        defaultSelectOutputAudioDevice={internalDefaultOutputAudioDeviceInfo}
        displayVideo={displayVideo}
        displayAudioDeviceList={displayAudioDeviceList}
        displayOutputAudioDeviceList={displayOutputAudioDeviceList}
        defaultEnableAudioStream={audioStreamEnabled}
        defaultEnableVideoStream={videoStreamEnabled}
        displayAudioButton={!!onAudioStreamEnabled}
        displayVideoButton={!!onVideoStreamEnabled}
        keepAlwaysAudioTrackAlive
        displayScreenSharingButton={false}
        useEmptyVideoTrackIfNoVideoTrack
        useEmptyAudioTrackIfNoAudioTrack
        callbacks={{
          media: (media) => onMediaStream?.(media),
          videoDevice: (device) => {
            if (device) setMediaParam(MediaKeys.DEFAULT_INPUT_VIDEO_DEVICE, device.label);
            onInputVideoDeviceInfo?.(device);
          },
          audioDevice: (device) => {
            if (device) setMediaParam(MediaKeys.DEFAULT_INPUT_AUDIO_DEVICE, device.label);
            onInputAudioDeviceInfo?.(device);
          },
          outputAudioDevice: (device) => {
            if (device) setMediaParam(MediaKeys.DEFAULT_OUTPUT_AUDIO_DEVICE, device.label);
            onOutputAudioDeviceInfo?.(device);
          },
          audioStreamEnable: (enabled) => onAudioStreamEnabled?.(enabled),
          videoStreamEnable: (enabled) => onVideoStreamEnabled?.(enabled),
          mediaCaptureError: (type, error) => {
            logger.error("Error capturing media - " + type + " : ", error);
            onMediaCaptureError?.(type as "video" | "audio", error);
          },
          emptyTrackError: (type, error) => {
            logger.error("Error generate empty track - " + type + " : ", error);
          },
        }}
        defaultMediaConstraint={getMediaConstraints(quality)}
      />
    );
  }
));
